// src/components/PlanEditor.jsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { SPACE_TYPES, UNCLASSIFIED, spaceOf, typeByKey } from '../utils/spaceTypes.js';
import { setDesignMassing } from '../utils/designStore';
import { IconSelect, IconAddPoint, IconSplit, IconMerge, IconUndo, IconRedo,
         IconDelete, IconSave, IconSaving, IconRectangle } from './TraceIcons.jsx';
import { polyArea, bbox, polyGap, polysOverlap, clipHalfPlane, dedupe, readingOrder } from '../utils/planGeometry.js';
import RoomTable, { RoomBadge } from './RoomTable.jsx';

/**
 * PlanEditor — the correction pass.
 *
 * Everything upstream exists to hand the architect something close enough that
 * fixing it beats drawing it. So this is the deliverable, not an afterthought:
 * a traced plan is a PROPOSAL, and the tools to disagree with it have to be as
 * good as the tools that produced it.
 *
 * Two rules shape the whole component:
 *
 *  - Rectangles are the base geometry. A room starts as four points and stays
 *    that way until the user asks for more, because a rectangle that is slightly
 *    wrong is far quicker to fix than a polygon that is slightly wrong.
 *
 *  - The wall gap is enforced, not advised. Neighbouring rooms are held one
 *    inner-wall thickness apart and the outline one outer-wall thickness. An
 *    edit that would overlap is REVERTED rather than accepted-and-flagged: the
 *    gaps are what the 3D pass extrudes as walls, so a plan that violates them
 *    cannot produce a building.
 *
 * All geometry is in image pixel space. Metres are display only, via `mpp`.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  dim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent)',
  warn: 'var(--warn)',
  err: 'var(--err)',
};

// label is kept: it is the accessible name and half of the tooltip. Only the
// visible text is dropped, never the thing a screen reader reads out.
const MODES = [
  ['select', 'Select',    'Drag a room, its corners, or its edges',  IconSelect],
  ['point',  'Add point', 'Click an edge to insert a corner',        IconAddPoint],
  ['split',  'Split',     'Click a room to cut it in two',           IconSplit],
  ['merge',  'Merge',     'Click two adjacent rooms to join them',   IconMerge],
  ['rect',   'Rectangle', 'Drag on the drawing to add a room the extraction missed', IconRectangle],
];

const HIST_LIMIT = 60;

// A region the classifier flagged as one of these is not a space to trace.
const NOT_A_ROOM = new Set(['border', 'titleBlock', 'furniture']);

/**
 * Rooms come in as detected polygons; simplify each to its rectangle.
 *
 * CONTAINERS ARE DROPPED. On a multi-plan sheet the CV finds the sheet border
 * and each panel frame as regions, and their rectangles enclose every room
 * inside them — so seeding them produced 54 overlapping pairs before the user
 * touched anything, and the wall-gap check then refused every edit.
 *
 * A region is a container if the classifier said so, if it swallows two or more
 * other regions, or if it covers most of the sheet. Dropping it here is safe:
 * nothing is lost that a user would want to drag, and what remains starts valid.
 */
function seedShapes(rooms, imageSize) {
  const list = rooms || [];
  const box = (r) => {
    const b = bbox(r.polygon || []);
    return [b.x0, b.y0, b.x1, b.y1];
  };
  const boxes = new Map(list.map((r) => [r.id, box(r)]));
  const sheetArea = Math.max(1, (imageSize?.w || 1) * (imageSize?.h || 1));

  const encloses = (a, b) => a[0] <= b[0] && a[1] <= b[1] && a[2] >= b[2] && a[3] >= b[3];

  return list
    .filter((r) => {
      if (NOT_A_ROOM.has(r.kind)) return false;
      const a = boxes.get(r.id);
      if ((a[2] - a[0]) * (a[3] - a[1]) > 0.40 * sheetArea) return false;
      let inside = 0;
      for (const q of list) {
        if (q.id !== r.id && encloses(a, boxes.get(q.id))) inside += 1;
        if (inside >= 2) return false;
      }
      return true;
    })
    .map((r) => {
      const [x0, y0, x1, y1] = boxes.get(r.id);
      const sp = spaceOf(r.label);
      return {
        id: r.id,
        pts: [[x0, y0], [x1, y0], [x1, y1], [x0, y1]],
        type: sp ? sp.key : null,
        label: r.label || null,
        panel: r.panel ?? null,
      };
    });
}

export default function PlanEditor({
  parse, mpp, std, imgSrc, isPdf, onCommit,
  editing, onToggleEdit, modelShown, onHideModel,
  showOffPlan, onToggleOffPlan, onSaveRooms, savingRooms, saveErr, detailsOpen,
  // Only ever handed straight to the design store, to stamp whose massing this
  // is. The editor itself has no use for it and must not grow one.
  projectId,
}) {
  const { w, h } = parse.imageSize || { w: 1000, h: 1000 };
  const svgRef = useRef(null);

  /**
   * The undo stack and the cursor into it are ONE state, not two.
   *
   * They used to be separate, and separate states can be written independently:
   * push() sliced the list back to the `hi` it had captured, while setHi()
   * incremented on its own. Two history writes in a single batch therefore left
   * the cursor past the end of the list, `hist[hi]` came back undefined, and the
   * next render died on `shapes.find`. Keeping them in one object makes the
   * cursor impossible to move without the list it points into.
   */
  const [past, setPast] = useState(() => ({ list: [seedShapes(parse.rooms, parse.imageSize)], i: 0 }));
  const hist = past.list;
  const hi = past.i;
  const shapes = hist[hi];

  const [mode, setMode] = useState('select');
  const [sel, setSel] = useState(null);          // shape id
  const [mergeFirst, setMergeFirst] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [ortho, setOrtho] = useState(true);
  const [bgOpacity, setBgOpacity] = useState(1);
  // showOffPlan is owned by PlanParseView so the drawing, the schedule AND the
  // 3D model all follow one flag. It used to be local here, which meant the two
  // views disagreed about what was hidden.
  const [built, setBuilt] = useState(null);
  const [datum, setDatum] = useState(null);
  const [floorH, setFloorH] = useState(3);
  const [showSlabs, setShowSlabs] = useState(true);
  const [place, setPlace] = useState({
    alongM: 0, insetM: 0, turns: 0, mirror: false, autoFit: true,
  });
  const drag = useRef(null);
  // The rubber band lives OUTSIDE `drag`: onMove returns early when drag.current
  // is null, and folding a second gesture into that ref would have every existing
  // handler treat a new rectangle as a room being moved.
  const rectStart = useRef(null);
  const [rectDraft, setRectDraft] = useState(null);

  // reseed when a different plan is parsed
  useEffect(() => {
    setPast({ list: [seedShapes(parse.rooms, parse.imageSize)], i: 0 });
    setSel(null);
  }, [parse]);

  // gap in pixels the standard demands between neighbouring rooms
  const gapPx = mpp && std ? std.inner / mpp : 0;
  const snapTol = Math.max(3, w / 300);

  // The cursor is not incremented, it is READ OFF the trimmed list. Trimming at
  // HIST_LIMIT used to be able to drop entries from the front while the cursor
  // counted up from the back, which is the same desync by another route.
  const push = useCallback((next) => {
    setPast(({ list, i }) => {
      const trimmed = list.slice(0, i + 1).concat([next]);
      const over = trimmed.length - HIST_LIMIT;
      const kept = over > 0 ? trimmed.slice(over) : trimmed;
      return { list: kept, i: kept.length - 1 };
    });
  }, []);

  const undo = useCallback(() => setPast((s) => ({ ...s, i: Math.max(0, s.i - 1) })), []);
  const redo = useCallback(
    () => setPast((s) => ({ ...s, i: Math.min(s.list.length - 1, s.i + 1) })), []);

  // report the confirmed plan upward whenever it settles
  useEffect(() => { onCommit?.(shapes); }, [shapes, onCommit]);

  useEffect(() => {
    // only a successful build has floors to report on
    const onBuilt = (e) => setBuilt(e.detail?.ok === false ? null : (e.detail || null));
    // The same totals the green summary shows, handed to the Assistant. A failed
    // build clears it rather than leaving the previous stack's numbers standing.
    const onBuiltForAssistant = (e) => setDesignMassing(
      e.detail?.ok === false ? null : (e.detail?.totals ? {
        floors: e.detail.totals.floors,
        builtUpM2: e.detail.totals.builtUpM2,
        groundCoverM2: e.detail.totals.groundCoverM2,
        totalHeightM: e.detail.totals.totalHeightM,
        areasTrustworthy: e.detail.totals.areasTrustworthy,
        perFloor: (e.detail.floors || []).map((f) => ({
          label: f.label, areaM2: f.areaM2, rooms: f.rooms, heightM: f.heightM,
        })),
        placement: e.detail.placement || null,
      } : null),
      projectId ?? null,
    );
    const onDatum = (e) => {
      setDatum(e.detail || null);
      // Picking an edge IS the decision not to auto-fit. Leaving autoFit set lit
      // both buttons while only one of them actually governed.
      if (e.detail) setPlace((p) => (p.autoFit ? { ...p, autoFit: false } : p));
    };
    window.addEventListener('traceMassingBuilt', onBuilt);
    window.addEventListener('traceMassingBuilt', onBuiltForAssistant);
    window.addEventListener('traceDatumPicked', onDatum);
    return () => {
      window.removeEventListener('traceMassingBuilt', onBuilt);
      window.removeEventListener('traceMassingBuilt', onBuiltForAssistant);
      window.removeEventListener('traceDatumPicked', onDatum);
    };
    // Re-subscribed on a project change so the handler stamps the CURRENT
    // project. Three listeners; cheaper than a ref to dodge a stale closure.
  }, [projectId]);


  /**
   * Accept an edit only if it keeps every wall gap. Reverting is deliberate:
   * an overlapping pair of rooms has no wall between them, so it cannot be
   * extruded, and letting it stand just moves the failure downstream.
   */
  const validate = useCallback((next, movedId, before) => {
    const moved = next.find((s) => s.id === movedId);
    if (!moved || moved.pts.length < 3) return 'a room needs at least three corners';
    const was = (before || []).find((s) => s.id === movedId);

    for (const other of next) {
      if (other.id === movedId || other.pts.length < 3) continue;
      const prev = (before || []).find((s) => s.id === other.id);

      // Judge the CHANGE, not the state. The extraction can hand over rooms that
      // already overlap, and refusing an edit for a violation that was there
      // before means the user can never fix it — the tool would block the very
      // correction it is asking for. So a pair that already clashed stays the
      // user's problem to resolve, and only NEW clashes are refused.
      const clashedBefore = was && prev && polysOverlap(was.pts, prev.pts);
      if (!clashedBefore && polysOverlap(moved.pts, other.pts)) {
        return `overlaps ${label(other)}`;
      }
      if (gapPx > 0 && !clashedBefore) {
        const gapWas = was && prev ? polyGap(was.pts, prev.pts) : Infinity;
        const gapNow = polyGap(moved.pts, other.pts);
        // tightening an already-tight gap is allowed as long as it is not made worse
        if (gapNow < gapPx * 0.75 && gapNow < gapWas - 0.5) {
          return `closer than one ${(std.inner * 1000).toFixed(0)} mm wall to ${label(other)}`;
        }
      }
    }
    return null;
  }, [gapPx, std]);

  const commit = useCallback((next, movedId, before) => {
    const bad = validate(next, movedId, before || shapes);
    if (bad) { setRefusal(bad); return false; }
    setRefusal(null);
    push(next);
    return true;
  }, [validate, push, shapes]);

  // ---- pointer plumbing -----------------------------------------------------

  const toSvg = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const m = svg.getScreenCTM();
    if (!m) return [0, 0];
    const p = pt.matrixTransform(m.inverse());
    return [p.x, p.y];
  }, []);

  /** Snap a moving coordinate so the gap to a neighbour is exactly one wall. */
  const snapAxis = useCallback((value, k, movingId) => {
    if (!gapPx) return value;
    let best = value;
    let bestD = snapTol;
    for (const s of shapes) {
      if (s.id === movingId) continue;
      for (const p of s.pts) {
        for (const cand of [p[k] - gapPx, p[k] + gapPx, p[k]]) {
          const d = Math.abs(cand - value);
          if (d < bestD) { bestD = d; best = cand; }
        }
      }
    }
    return best;
  }, [shapes, gapPx, snapTol]);

  const onDown = useCallback((e, kind, shapeId, index) => {
    // Rectangle mode belongs to the canvas, not to the rooms. Without this the
    // stopPropagation below eats the press and a drag started over an existing
    // room MOVES that room instead of drawing — and untraced space is usually
    // right next to traced space, so that is where people will start.
    if (mode === 'rect') return;
    e.stopPropagation();
    const at = toSvg(e);

    if (mode === 'merge') {
      if (!mergeFirst) { setMergeFirst(shapeId); setSel(shapeId); return; }
      if (mergeFirst === shapeId) { setMergeFirst(null); return; }
      doMerge(mergeFirst, shapeId);
      return;
    }
    if (mode === 'split' && kind === 'shape') { doSplit(shapeId, at); return; }
    if (mode === 'point' && kind === 'edge') { doAddPoint(shapeId, index, at); return; }

    setSel(shapeId);
    drag.current = { kind, shapeId, index, start: at, before: shapes };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [mode, mergeFirst, shapes, toSvg]);

  const onMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    const at = toSvg(e);
    const dx = at[0] - d.start[0];
    const dy = at[1] - d.start[1];

    setPast((st) => {
      const cur = st.list.slice();
      const live = d.before.map((s) => {
        if (s.id !== d.shapeId) return s;
        let pts = s.pts.map((p) => p.slice());

        if (d.kind === 'shape') {
          pts = pts.map((p) => [p[0] + dx, p[1] + dy]);
        } else if (d.kind === 'vertex') {
          const p = pts[d.index];
          pts[d.index] = [
            snapAxis(p[0] + dx, 0, s.id),
            snapAxis(p[1] + dy, 1, s.id),
          ];
        } else if (d.kind === 'edge') {
          // move the whole edge parallel to itself
          const i = d.index;
          const j = (i + 1) % pts.length;
          const a = pts[i];
          const b = pts[j];
          const vertical = Math.abs(a[0] - b[0]) < Math.abs(a[1] - b[1]);
          if (vertical || !ortho) {
            const nx = snapAxis(a[0] + dx, 0, s.id);
            pts[i] = [nx, a[1] + (ortho ? 0 : dy)];
            pts[j] = [nx, b[1] + (ortho ? 0 : dy)];
          }
          if (!vertical || !ortho) {
            const ny = snapAxis(a[1] + dy, 1, s.id);
            pts[i] = [pts[i][0] + (ortho ? 0 : dx), ny];
            pts[j] = [pts[j][0] + (ortho ? 0 : dx), ny];
          }
        }
        return { ...s, pts };
      });
      cur[st.i] = live;                   // live preview, not a history entry
      return { ...st, list: cur };
    });
  }, [toSvg, hi, ortho, snapAxis]);

  const onUp = useCallback(() => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    const moved = hist[hi];
    // put the pre-drag state back, then commit through validation
    setPast((s) => { const c = s.list.slice(); c[s.i] = d.before; return { ...s, list: c }; });
    commit(moved, d.shapeId, d.before);
  }, [hist, hi, commit]);

  // ---- operations -----------------------------------------------------------

  function doAddPoint(shapeId, edgeIdx, at) {
    const next = shapes.map((s) => {
      if (s.id !== shapeId) return s;
      const pts = s.pts.slice();
      pts.splice(edgeIdx + 1, 0, [at[0], at[1]]);
      return { ...s, pts };
    });
    commit(next, shapeId);
    setMode('select');
  }

  /**
   * Draw a room the extraction missed.
   *
   * Filed under the panel whose box CONTAINS THE RECTANGLE'S CENTRE — the same
   * rule planPanels.js uses server-side. Getting this wrong is not cosmetic: a
   * room with panel null is off-plan, so it is hidden from the schedule and never
   * extruded, and the user would have drawn a rectangle that silently does
   * nothing.
   *
   * Routed through commit() like every other edit, so a rectangle drawn across a
   * neighbour is refused by the same wall-gap rule rather than being a way round
   * it.
   */
  function addRect(x0, y0, x1, y1) {
    const ax = Math.min(x0, x1); const bx = Math.max(x0, x1);
    const ay = Math.min(y0, y1); const by = Math.max(y0, y1);
    // Smaller than the snap tolerance is a click that slipped, not a room.
    if (bx - ax < snapTol || by - ay < snapTol) { setRectDraft(null); return; }

    const cx = (ax + bx) / 2;
    const cy = (ay + by) / 2;
    const panels = parse.panels || [];
    const hit = panels.find(
      (pn) => cx >= pn.box[0] && cx <= pn.box[0] + pn.box[2] &&
              cy >= pn.box[1] && cy <= pn.box[1] + pn.box[3]
    );
    if (panels.length && !hit) {
      setRectDraft(null);
      setRefusal('that is off the plan — draw inside one of the floor panels');
      return;
    }

    const taken = new Set(shapes.map((s) => s.id));
    let k = 1;
    while (taken.has(`new${k}`)) k += 1;

    const next = shapes.concat([{
      id: `new${k}`,
      pts: [[ax, ay], [bx, ay], [bx, by], [ax, by]],
      type: null,
      label: null,
      panel: hit ? hit.index : null,
    }]);
    setRectDraft(null);
    if (commit(next, `new${k}`)) {
      setSel(`new${k}`);
      setMode('select');
    }
  }


  function doSplit(shapeId, at) {
    const s = shapes.find((x) => x.id === shapeId);
    if (!s) return;
    const b = bbox(s.pts);
    // cut across the SHORT axis, the way a room is actually divided
    const axis = (b.x1 - b.x0) >= (b.y1 - b.y0) ? 'x' : 'y';
    const cut = axis === 'x' ? at[0] : at[1];
    const half = gapPx / 2 || 0.5;
    const left = dedupe(clipHalfPlane(s.pts, axis, cut - half, 'lt'));
    const right = dedupe(clipHalfPlane(s.pts, axis, cut + half, 'gt'));
    if (left.length < 3 || right.length < 3) { setRefusal('cut falls outside the room'); return; }
    if (polyArea(left) < 4 || polyArea(right) < 4) { setRefusal('one side would be empty'); return; }
    const next = shapes.flatMap((x) =>
      x.id !== shapeId ? [x] : [
        { ...x, id: `${x.id}a`, pts: left },
        { ...x, id: `${x.id}b`, pts: right },
      ]
    );
    setRefusal(null);
    push(next);
    setSel(`${shapeId}a`);
    setMode('select');
  }

  /**
   * Merge two rooms into the rectangle that covers both.
   *
   * Deliberately limited: a true polygon union needs a clipping library, and the
   * case that actually occurs is one room detected as two — furniture or a
   * doorway fragmenting it — where the covering rectangle IS the right answer.
   * Anything else is refused rather than approximated.
   */
  function doMerge(idA, idB) {
    const a = shapes.find((s) => s.id === idA);
    const b = shapes.find((s) => s.id === idB);
    setMergeFirst(null);
    if (!a || !b) return;
    if (gapPx && polyGap(a.pts, b.pts) > gapPx * 3) {
      setRefusal('those rooms are not adjacent'); return;
    }
    const ba = bbox(a.pts);
    const bb = bbox(b.pts);
    const pts = [
      [Math.min(ba.x0, bb.x0), Math.min(ba.y0, bb.y0)],
      [Math.max(ba.x1, bb.x1), Math.min(ba.y0, bb.y0)],
      [Math.max(ba.x1, bb.x1), Math.max(ba.y1, bb.y1)],
      [Math.min(ba.x0, bb.x0), Math.max(ba.y1, bb.y1)],
    ];
    const merged = { ...a, pts };
    const next = shapes.filter((s) => s.id !== idB).map((s) => (s.id === idA ? merged : s));
    if (commit(next, idA)) { setSel(idA); setMode('select'); }
  }

  /**
   * Rename a room. The space type — and so its colour — is re-derived from the
   * name, so typing "Master Bedroom" turns the rectangle indigo without the user
   * touching a separate type control.
   */
  function renameRoom(id, name) {
    const sp = spaceOf(name);
    push(shapes.map((s) => (s.id === id ? { ...s, label: name || null, type: sp ? sp.key : null } : s)));
  }

  /**
   * Resize from a typed dimension. The room's top-left stays put and the
   * opposite edges move, which is what a user means by "make this 4.2 m wide".
   *
   * Routed through commit(), so a typed value that would overlap a neighbour is
   * refused exactly like a dragged one — the table cannot be used as a way
   * around the wall-gap rule.
   */
  function resizeRoom(id, axis, metres) {
    if (!mpp) return;
    const target = metres / mpp;
    const next = shapes.map((s) => {
      if (s.id !== id) return s;
      const b = bbox(s.pts);
      const cur = axis === 'w' ? b.x1 - b.x0 : b.y1 - b.y0;
      if (cur <= 0) return s;
      const k = target / cur;
      return {
        ...s,
        pts: s.pts.map(([x, y]) => (axis === 'w'
          ? [b.x0 + (x - b.x0) * k, y]
          : [x, b.y0 + (y - b.y0) * k])),
      };
    });
    commit(next, id);
  }

  function setType(key) {
    push(shapes.map((s) => (s.id === sel ? { ...s, type: key || null } : s)));
  }

  function removeSelected() {
    if (!sel) return;
    push(shapes.filter((s) => s.id !== sel));
    setSel(null);
  }

  // ---- keyboard -------------------------------------------------------------

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeSelected(); }
      else if (e.key === 'Escape') { setSel(null); setMergeFirst(null); setMode('select'); }
      else if (e.key === 'Shift') setOrtho(false);
    };
    const onKeyUp = (e) => { if (e.key === 'Shift') setOrtho(true); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  });

  // ---- render ---------------------------------------------------------------

  const selShape = shapes.find((s) => s.id === sel);
  const selBox = selShape ? bbox(selShape.pts) : null;
  const hs = Math.max(3, w / 190);            // handle size in viewBox units
  const sw = Math.max(1, w / 700);

  // One ordering feeds both the badges on the plan and the rows in the table.
  const numbered = useMemo(
    () => readingOrder(shapes.filter((s) => s.pts.length >= 3), h),
    [shapes, h]
  );

  // Rooms that already overlap something. These come from the extraction, not
  // from any edit — two regions detected for one space, usually nested. They
  // are surfaced rather than auto-deleted because there is no reliable way to
  // tell which of a nested pair is the real room, and guessing would quietly
  // destroy correct geometry.
  const hasPanels = (parse.panels || []).length > 0;
  const visible = useMemo(
    () => numbered.filter((r) => showOffPlan || !hasPanels || r.panel != null),
    [numbered, showOffPlan, hasPanels]
  );

  /**
   * Unsaved work is the distance from the seed, not the length of the history:
   * undo all the way back leaves the extraction exactly as it was found, and
   * calling that "unsaved" would ask the user to save nothing.
   */
  const dirty = shapes !== hist[0];

  /**
   * The rooms as they should be stored.
   *
   * The editor is not given every region. seedShapes drops the sheet border and
   * the panel frames, so a room missing from `shapes` is either one the user
   * deleted or one the editor never held — and only this side knows which. The
   * seed ids settle it, so containers survive a save untouched and a deletion
   * still deletes.
   */
  const roomsForSave = useCallback(() => {
    const edited = new Map(shapes.map((s) => [s.id, s]));
    const seedIds = new Set(hist[0].map((s) => s.id));
    const orig = parse.rooms || [];
    const known = new Set(orig.map((r) => r.id));
    const kept = orig
      .filter((r) => !seedIds.has(r.id) || edited.has(r.id))
      .map((r) => {
        const s = edited.get(r.id);
        if (!s) return { id: r.id, polygon: r.polygon, label: r.label ?? null, panel: r.panel ?? null };
        return { id: s.id, polygon: s.pts.map(([x, y]) => [x, y]),
                 label: s.label ?? null, panel: s.panel ?? null, edited: true };
      });
    // Split makes rooms the extraction never had.
    const added = shapes
      .filter((s) => !known.has(s.id))
      .map((s) => ({ id: s.id, polygon: s.pts.map(([x, y]) => [x, y]),
                     label: s.label ?? null, panel: s.panel ?? null,
                     kind: 'room', edited: true }));
    return kept.concat(added);
  }, [shapes, hist, parse.rooms]);

  const saveRooms = useCallback(() => {
    onSaveRooms?.(roomsForSave());
    // Nothing is cleared here. The save either lands — and the new parse reseeds
    // this editor through the effect above, clearing `dirty` on its own — or it
    // fails and the edits must still be on screen to try again.
  }, [onSaveRooms, roomsForSave]);

  /** Back to the extraction, history and all. */
  const resetRooms = useCallback(() => {
    setPast((s) => ({ list: [s.list[0]], i: 0 }));
    setSel(null);
    setRefusal(null);
  }, []);


  /**
   * Send the CURRENT rectangles to the 3D view.
   *
   * Only rooms on a panel are sent: the sheet border and the gaps between plans
   * are paper, and extruding them would put a 762 m2 slab over the site.
   */
  const buildIn3D = useCallback(() => {
    // Off-plan regions follow the schedule's toggle: hidden by default, and
    // extruded only when the user has asked to see them. Modelling the sheet
    // border unasked drops a 762 m² slab over the site.
    const rooms = numbered.filter((r) => showOffPlan || !hasPanels || r.panel != null);
    window.dispatchEvent(new CustomEvent('buildTraceMassing', {
      detail: {
        parse, rooms, mpp, std, floorHeightM: floorH,
        alongM: place.alongM, insetM: place.insetM,
        quarterTurns: place.turns, mirror: place.mirror,
        autoFit: place.autoFit,
      },
    }));
    // Nothing is marked as shown here. PlanParseView sets that from the
    // traceMassingBuilt report, so the button reflects the outcome rather than
    // the attempt.
  }, [numbered, hasPanels, parse, mpp, std, floorH, showOffPlan, place]);

  /**
   * Show hides, hide rebuilds. Rebuilding on every show rather than just
   * un-hiding is deliberate: the rooms may have been edited since, and a model
   * that silently lags the table is worse than one that takes a moment.
   */
  // Flipping the off-plan toggle while the model is up rebuilds it, so the 3D
  // and the schedule never show different sets of rooms.
  //
  // Room edits rebuild it too. A size typed into the table reaches the model
  // without a second click, which is what makes the table usable for trying
  // space plans rather than only recording one.
  useEffect(() => {
    if (!modelShown) return undefined;
    // Never mid-drag. A dragged corner rewrites the rectangles on every pointer
    // frame, and extruding the whole stack per frame would stall the drag. onUp
    // commits once on release, and that commit is what arrives here.
    if (drag.current) return undefined;
    // Coalesce the burst: a release fires a restore and a commit back to back,
    // and tabbing along a row commits every cell it leaves. One rebuild each.
    const t = setTimeout(buildIn3D, 200);
    return () => clearTimeout(t);
    // buildIn3D is deliberately absent: it is rebuilt on every render whose
    // inputs changed, and listing it would rebuild the model on renders that
    // changed nothing about the rooms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbered, showOffPlan, place, floorH]);

  const toggleModel = useCallback(() => {
    if (modelShown) onHideModel?.();
    else buildIn3D();
  }, [modelShown, onHideModel, buildIn3D]);

  const clashing = useMemo(() => {
    const bad = new Set();
    // only among visible rooms: warning about a clash with a hidden sheet border
    // would send the user hunting for a rectangle that is not on screen
    const pool = visible;
    for (let i = 0; i < pool.length; i++) {
      for (let j = i + 1; j < pool.length; j++) {
        const a = pool[i];
        const b = pool[j];
        if (a.pts.length < 3 || b.pts.length < 3) continue;
        if (polysOverlap(a.pts, b.pts)) { bad.add(a.id); bad.add(b.id); }
      }
    }
    return bad;
  }, [visible]);

  const dims = useMemo(() => {
    if (!selShape) return null;
    const b = bbox(selShape.pts);
    const a = polyArea(selShape.pts);
    return mpp
      ? { w: ((b.x1 - b.x0) * mpp).toFixed(2), h: ((b.y1 - b.y0) * mpp).toFixed(2),
          a: (a * mpp * mpp).toFixed(1), unit: 'm' }
      : { w: Math.round(b.x1 - b.x0), h: Math.round(b.y1 - b.y0),
          a: Math.round(a).toLocaleString(), unit: 'px' };
  }, [selShape, mpp]);

  return (
    <div style={S.wrap}>
      {mode === 'merge' && (
        <div style={S.tip}>
          {mergeFirst ? 'Now click the room to join it with.' : 'Click the first of two adjacent rooms.'}
        </div>
      )}

      {built && detailsOpen && (
        <div style={S.builtBox}>
          <b>Built {built.totals.floors} floors</b> · {built.totals.builtUpM2} m² built-up ·
          {' '}{built.totals.groundCoverM2} m² ground cover · {built.totals.totalHeightM} m tall
          <div style={{ marginTop: 4 }}>
            {built.floors.map((f) => (
              <span key={f.index}
                    style={{ ...S.floorChip,
                             ...(f.overlapRatio > 1.02 ? { color: T.warn } : null) }}
                    title={`${f.rooms} rooms in a ${f.extentM2} m² floor`}>
                {f.label} {f.areaM2} m² ({f.rooms})
                {f.overlapRatio > 1.02 ? ` ×${f.overlapRatio}` : ''}
              </span>
            ))}
          </div>
          {!built.totals.areasTrustworthy && (
            <div style={{ marginTop: 5, color: T.warn }}>
              Room areas exceed the floor they sit on, so these rooms overlap and
              the built-up figure is an upper bound, not a measurement. Fix the
              overlapping rectangles before this feeds a compliance check.
            </div>
          )}
          {built.placement?.autoFit ? (
            <div style={{ marginTop: 4, color: T.dim }}>
              Centred on the site and turned {built.placement.rotationDeg}° so the
              model's long axis runs along the site's.
              {built.placement.boxM && (
                <> Model box {built.placement.boxM[0]} × {built.placement.boxM[1]} m
                  in a {built.placement.siteLongM} × {built.placement.siteShortM} m site.</>
              )}
              {!built.placement.fits && (
                <span style={{ color: T.err }}>
                  {' '}The plan does not fit inside the buildable area at any rotation.
                </span>
              )}
              {' '}Pick a setback edge to place it against a boundary instead.
            </div>
          ) : built.placement?.onEdge ? (
            <div style={{ marginTop: 4, color: T.dim }}>
              Laid on the picked setback edge, rotated {built.placement.rotationDeg}°
              {built.placement.reversed ? ' (flipped to face inward)' : ''}.
              {built.placement.outside > 0 && (
                <span style={{ color: T.err }}>
                  {' '}{built.placement.outside} of 4 corners fall OUTSIDE the
                  setback — the plan is larger than the buildable area, or the
                  wrong edge was picked.
                </span>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 4, color: T.warn }}>
              No datum edge picked, so the stack is only centred on the site.
              Pick a setback edge to place it properly.
            </div>
          )}
          {built.alignment.some((a) => Math.abs(a.dx) > 0.5 || Math.abs(a.dz) > 0.5) && (
            <div style={{ marginTop: 5, color: T.warn }}>
              Floors do not line up: {built.alignment
                .filter((a) => Math.abs(a.dx) > 0.5 || Math.abs(a.dz) > 0.5)
                .map((a) => `${a.label} off by ${a.dx}, ${a.dz} m`).join('; ')}.
              Panels were registered on their frames, which assumes each plan sits
              the same way inside its own frame.
            </div>
          )}
        </div>
      )}
      {clashing.size > 0 && (
        <div style={S.clash}>
          <b>{clashing.size} rooms overlap</b> — from the extraction, not your edits.
          They are outlined in red. Delete the wrong one, or merge the pair.
        </div>
      )}
      {refusal && (
        <div style={S.refusal}>
          Edit refused — {refusal}. The gaps between rooms are the walls, so an
          overlap can’t be extruded later.
        </div>
      )}
      {saveErr && (
        <div style={S.refusal}>
          Rooms not saved — {saveErr}. The corrections are still here; try Save
          again, and do not leave the editor until it succeeds.
        </div>
      )}

      <div style={S.compare}>
        <div style={S.drawingCol}>
          <div style={S.traceBlock}>
            {/* The tracing's own header, the counterpart of the Room schedule's.
                These seven act on the tracing and nothing else — what they change
                is the linework underneath them. */}
            <div style={S.traceHead}>
              <span style={S.traceTitle}>Image tracing</span>
              {MODES.map(([k, lbl, tip, Icon]) => (
                <button key={k} title={`${lbl} — ${tip}`} aria-label={lbl}
                        aria-pressed={mode === k}
                        onClick={() => { setMode(k); setMergeFirst(null); }}
                        style={{ ...S.tool, ...S.iconTool, ...(mode === k ? S.toolOn : null) }}>
                  <Icon />
                </button>
              ))}
              <span style={S.sep} />
              {/* Disabled, not dimmed-but-clickable: a greyed control that still fires is
                  worse than one that visibly cannot. */}
              <button style={{ ...S.tool, ...S.iconTool, ...(hi > 0 ? null : S.toolOff) }}
                      onClick={undo} disabled={!(hi > 0)}
                      title="Undo — Ctrl+Z" aria-label="Undo">
                <IconUndo />
              </button>
              <button style={{ ...S.tool, ...S.iconTool, ...(hi < hist.length - 1 ? null : S.toolOff) }}
                      onClick={redo} disabled={!(hi < hist.length - 1)}
                      title="Redo — Ctrl+Shift+Z" aria-label="Redo">
                <IconRedo />
              </button>
              <button style={{ ...S.tool, ...S.iconTool, ...(sel ? null : S.toolOff) }}
                      onClick={removeSelected} disabled={!sel}
                      title={sel ? 'Delete the selected room' : 'Select a room first'}
                      aria-label="Delete room">
                <IconDelete />
              </button>
              <span style={{ flex: 1 }} />
              {/* Save keeps its word. It is the one button here that writes to the server,
                  and the only one whose effect this toolbar cannot undo. */}
              <button
                style={{ ...S.tool, ...(dirty && !savingRooms ? S.save : S.toolOff) }}
                onClick={dirty && !savingRooms ? saveRooms : undefined}
                disabled={!dirty || savingRooms}
                title={dirty
                  ? 'Store the traced rooms against the drawing'
                  : 'Nothing has changed since the last save'}
              >
                <span style={S.saveInner}>
                  {savingRooms ? <IconSaving /> : <IconSave />}
                  {savingRooms ? 'Saving…' : 'Save'}
                </span>
              </button>
            </div>
            {/* How the tracing is DRAWN, not what it contains — its own row below
                the tools rather than mixed in among them. */}
            <div style={S.traceSub}>
              <label style={S.hint} title="Fade the drawing behind the tracing">
                drawing{' '}
                <input
                  type="range" min="0.15" max="1" step="0.05" value={bgOpacity}
                  onChange={(e) => setBgOpacity(Number(e.target.value))}
                  style={{ width: 64, verticalAlign: 'middle', accentColor: T.accent }}
                />
              </label>
              <span style={S.hint}>{ortho ? 'orthogonal · Shift for free' : 'free movement'}</span>
            </div>
            <div style={S.canvasBox}>
              {/* The drawing sits behind the tracing so the user can see whether a
                  rectangle actually covers the room it claims. Shown for PDFs too —
                  the raster endpoint serves the exact render the geometry was
                  measured on, so the two line up pixel for pixel. */}
              <img src={imgSrc} alt=""
                   onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                   style={{ position: 'absolute', top: 0, left: 0, width: '100%',
                            display: 'block', opacity: bgOpacity }} />
              <svg
                ref={svgRef}
                viewBox={`0 0 ${w} ${h}`}
                onPointerMove={(e) => {
                  if (rectStart.current) {
                    const at = toSvg(e);
                    setRectDraft({ x0: rectStart.current[0], y0: rectStart.current[1],
                                   x1: at[0], y1: at[1] });
                    return;
                  }
                  onMove(e);
                }}
                onPointerUp={(e) => {
                  if (rectStart.current) {
                    const s0 = rectStart.current;
                    rectStart.current = null;
                    const at = toSvg(e);
                    addRect(s0[0], s0[1], at[0], at[1]);
                    return;
                  }
                  onUp(e);
                }}
                onPointerLeave={(e) => {
                  // Abandon a half-drawn rectangle rather than committing one whose far corner
                  // was never placed.
                  if (rectStart.current) { rectStart.current = null; setRectDraft(null); return; }
                  onUp(e);
                }}
                onPointerDown={(e) => {
                  setSel(null);
                  setMergeFirst(null);
                  if (mode !== 'rect') return;
                  const at = toSvg(e);
                  rectStart.current = at;
                  setRectDraft({ x0: at[0], y0: at[1], x1: at[0], y1: at[1] });
                  e.currentTarget.setPointerCapture?.(e.pointerId);
                }}
                style={{
                  width: '100%', display: 'block', touchAction: 'none',
                  position: 'relative', background: 'transparent',
                  cursor: mode === 'select' ? 'default' : 'crosshair',
                }}
              >
                {visible.map((s) => {
                  const t = typeByKey(s.type);
                  const col = t ? t.color : UNCLASSIFIED;
                  const on = s.id === sel;
                  const pending = s.id === mergeFirst;
                  return (
                    <g key={s.id}>
                      <polygon
                        points={s.pts.map((p) => p.join(',')).join(' ')}
                        // Unclassified rooms keep a neutral wash so they can still be
                        // picked up and dragged — an unfilled polygon is both invisible
                        // and almost impossible to hit on a busy drawing.
                        fill={col}
                        fillOpacity={t ? (on ? 0.34 : 0.2) : (on ? 0.2 : 0.09)}
                        stroke={pending ? '#fff' : clashing.has(s.id) ? T.err : col}
                        strokeWidth={on || pending || clashing.has(s.id) ? sw * 2.4 : sw}
                        strokeDasharray={t ? undefined : `${w / 150} ${w / 220}`}
                        onPointerDown={(e) => onDown(e, 'shape', s.id)}
                        style={{ cursor: mode === 'select' ? 'move' : 'crosshair' }}
                      />
                      {/* edge grips: drag to move a wall, or click to add a corner */}
                      {on && s.pts.map((p, i) => {
                        const q = s.pts[(i + 1) % s.pts.length];
                        return (
                          <line
                            key={`e${i}`}
                            x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]}
                            stroke="transparent" strokeWidth={hs * 1.6}
                            onPointerDown={(e) => onDown(e, 'edge', s.id, i)}
                            style={{ cursor: mode === 'point' ? 'copy' : 'grab' }}
                          />
                        );
                      })}
                      {on && s.pts.map((p, i) => (
                        <rect
                          key={`v${i}`}
                          x={p[0] - hs / 2} y={p[1] - hs / 2} width={hs} height={hs}
                          fill="#fff" stroke={col} strokeWidth={sw}
                          onPointerDown={(e) => onDown(e, 'vertex', s.id, i)}
                          style={{ cursor: 'grab' }}
                        />
                      ))}
                    </g>
                  );
                })}
                {visible.map((r) => (
                  <RoomBadge key={`n${r.id}`} room={r} w={w} dimmed={sel && r.id !== sel} />
                ))}
                {rectDraft && (
                  <rect x={Math.min(rectDraft.x0, rectDraft.x1)}
                        y={Math.min(rectDraft.y0, rectDraft.y1)}
                        width={Math.abs(rectDraft.x1 - rectDraft.x0)}
                        height={Math.abs(rectDraft.y1 - rectDraft.y0)}
                        fill={T.accent} fillOpacity="0.16"
                        stroke={T.accent} strokeWidth={sw * 0.9}
                        strokeDasharray={`${w / 180} ${w / 240}`} pointerEvents="none" />
                )}
                {selBox && (
                  <rect x={selBox.x0} y={selBox.y0}
                        width={selBox.x1 - selBox.x0} height={selBox.y1 - selBox.y0}
                        fill="none" stroke={T.accent} strokeWidth={sw * 0.8}
                        strokeDasharray={`${w / 200} ${w / 260}`} pointerEvents="none" />
                )}
              </svg>
            </div>
          </div>
          <div style={S.inspector}>
            {selShape ? (
              <>
                <select style={S.select} value={selShape.type || ''} onChange={(e) => setType(e.target.value)}>
                  <option value="">Unclassified</option>
                  {SPACE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
                <span style={S.readout}>
                  {dims.w} × {dims.h} {dims.unit} · {dims.a} {dims.unit}²
                </span>
                <span style={S.dim}>{selShape.pts.length} corners</span>
              </>
            ) : (
              <span style={S.dim}>
                Select a room to retype or resize it.
                {gapPx > 0
                  ? ` Rooms are held ${(std.inner * 1000).toFixed(0)} mm apart — one inner wall.`
                  : ' Set a scale to enforce wall gaps.'}
              </span>
            )}
            <span style={{ flex: 1 }} />
            <span style={S.dim}>{shapes.length} rooms</span>
          </div>
        </div>
        <RoomTable
          rooms={numbered}
          panels={parse.panels}
          mpp={mpp}
          selectedId={sel}
          onSelect={setSel}
          onRename={renameRoom}
          onResize={resizeRoom}
          showOffPlan={showOffPlan}
          onToggleOffPlan={onToggleOffPlan}
          editing={editing}
          onToggleEdit={onToggleEdit}
          dirty={dirty}
          onSave={saveRooms}
          onReset={resetRooms}
          saving={savingRooms}
          onModel={toggleModel}
          modelShown={modelShown}
          afterHead={/* Placement belongs to the model, so it sits under the
                        button that makes one and only once there is something
                        to place. */
            mpp && modelShown ? (
          <div style={S.placeBox}>
            <div style={S.placeHead}>Place on site</div>
            <div style={S.placeRow}>
              <label style={S.hint} title="Floor-to-floor height for every traced floor">
                floor{' '}
                <input
                  type="number" min="2.1" max="6" step="0.1" value={floorH}
                  onChange={(e) => setFloorH(Math.max(2.1, Number(e.target.value) || 3))}
                  style={S.numIn}
                /> m
              </label>
              <label style={S.hint} title="The plate under each floor is the bounding extent of that floor's rooms, so it always reaches past them">
                <input
                  type="checkbox"
                  checked={showSlabs}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setShowSlabs(on);
                    window.dispatchEvent(new CustomEvent('traceSlabVisibility', {
                      detail: { visible: on },
                    }));
                  }}
                  style={{ verticalAlign: 'middle', marginRight: 4, accentColor: T.accent }}
                />
                floor plates
              </label>
            </div>
            <div style={S.placeRow}>
              <span style={S.step}>0</span>
              <button
                style={{ ...S.tool, ...(place.autoFit && !datum ? S.datumOn : null) }}
                onClick={() => {
                  // The two are alternatives, so choosing this one has to release the edge:
                  // the 3D view holds the datum in a ref and would otherwise go on
                  // honouring it forever.
                  const on = !(place.autoFit && !datum);
                  if (on) {
                    setDatum(null);
                    window.dispatchEvent(new CustomEvent('clearTraceDatum'));
                  }
                  setPlace((p) => ({ ...p, autoFit: on }));
                }}
                title={datum
                  ? 'Centre on the plot instead, releasing the picked edge'
                  : 'Centre the blocks on the plot, turned along its long axis'}
              >
                Auto-fit to site
              </button>
              <span style={S.hint}>
                centres the blocks on the plot and turns them along its long axis
              </span>
            </div>

            <div style={S.placeRow}>
              <span style={S.step}>1</span>
              <button
                style={{ ...S.tool, ...(datum ? S.datumOn : S.stepTodo) }}
                onClick={() => window.dispatchEvent(new CustomEvent('pickTraceDatum'))}
              >
                {datum ? `Edge ${datum.edgeIndex} · ${datum.lengthM} m` : 'Pick the setback edge'}
              </button>
              <span style={S.hint}>the plan's front sits on this edge</span>
            </div>

            <div style={{ ...S.placeRow, opacity: datum ? 1 : 0.45 }}>
              <span style={S.step}>2</span>
              <span style={S.hint}>face</span>
              <button style={S.tool} disabled={!datum}
                      onClick={() => setPlace((p) => ({ ...p, turns: (p.turns + 1) % 4 }))}>
                rotate {place.turns * 90}°
              </button>
              <button style={{ ...S.tool, ...(place.mirror ? S.datumOn : null) }} disabled={!datum}
                      onClick={() => setPlace((p) => ({ ...p, mirror: !p.mirror }))}>
                mirror
              </button>
            </div>

            <div style={{ ...S.placeRow, opacity: datum ? 1 : 0.45 }}>
              <span style={S.step}>3</span>
              <span style={S.hint}>along</span>
              <input type="number" step="0.5" value={place.alongM} disabled={!datum}
                     onChange={(e) => setPlace((p) => ({ ...p, alongM: Number(e.target.value) || 0 }))}
                     style={S.numIn} />
              <span style={S.hint}>m · in from edge</span>
              <input type="number" step="0.5" value={place.insetM} disabled={!datum}
                     onChange={(e) => setPlace((p) => ({ ...p, insetM: Number(e.target.value) || 0 }))}
                     style={S.numIn} />
              <span style={S.hint}>m</span>
            </div>
          </div>
          ) : null}
        />
      </div>
    </div>
  );
}

function label(s) {
  const t = typeByKey(s.type);
  return t ? t.name.toLowerCase() : (s.id || 'another room');
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  /* Canvas beside the table, same as the Check view: you correct a room against
     the drawing, so the two want to be on screen together. auto-fit stacks them
     back into the old order when there is no room for two 380px columns. */
  /* 340, not 380. The drawer is main-pane width minus its padding — about 758px
     on a 1470px window — and two 380px columns plus the gap needed 772, so the
     pair silently stacked and the schedule dropped below the fold. 340 leaves
     real headroom while still stacking on a genuinely narrow panel. */
  compare: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 10, alignItems: 'start' },
  /* The inspector edits whatever is selected, so it rides with the canvas and
     stays put while the table is scrolled. */
  drawingCol: { display: 'flex', flexDirection: 'column', gap: 8,
                position: 'sticky', top: 0 },
  /* The tracing's header, matched to the Room schedule's so the two sections
     read as a pair: a title on the left, the controls that act on that section
     on the right, sitting on the section it governs. */
  traceHead: { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap',
               background: T.surface, border: `1px solid ${T.border}`,
               borderRadius: '8px 8px 0 0', borderBottom: 'none',
               padding: '6px 9px' },
  traceTitle: { fontSize: 11.5, fontWeight: 600, color: T.text, marginRight: 4 },
  /* Second row: how the tracing is drawn. Square top and bottom because it is
     sandwiched between the header and the drawing. */
  traceSub: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              background: T.surface, borderLeft: `1px solid ${T.border}`,
              borderRight: `1px solid ${T.border}`, padding: '4px 9px' },
  save: { background: 'var(--ok)', border: '1px solid var(--ok)', color: '#fff' },
  tool: { fontSize: 10.5, fontWeight: 600, padding: '5px 10px', borderRadius: 7,
          cursor: 'pointer', border: `1px solid ${T.border}`,
          background: 'var(--btn-face,#313d4b)', color: T.text },
  toolOn: { background: T.accent, border: `1px solid ${T.accent}`, color: '#fff' },
  /* Icon buttons are square and centred. 30px keeps them above the 24px floor
     a pointer target wants while staying smaller than the labelled Save. */
  iconTool: { padding: 0, width: 30, height: 26, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center' },
  /* Unavailable, and saying so. The old 0.35 opacity dimmed the button but left
     it clickable, so Delete with nothing selected still looked like a thing you
     could press. */
  toolOff: { opacity: 0.4, cursor: 'not-allowed', background: 'var(--btn-face,#313d4b)',
             border: `1px solid ${T.border}`, color: T.text },
  saveInner: { display: 'inline-flex', alignItems: 'center', gap: 5 },
  sep: { width: 1, height: 16, background: T.border, margin: '0 3px' },
  hint: { fontSize: 9.5, color: T.dim },
  tip: { fontSize: 10.5, color: T.text, background: 'rgba(57,135,229,0.12)',
         border: `1px solid ${T.accent}`, borderRadius: 7, padding: '6px 9px', maxWidth: 560 },
  numIn: { width: 46, fontSize: 10.5, padding: '2px 4px', borderRadius: 5,
           border: `1px solid ${T.border}`, background: 'rgba(0,0,0,0.28)',
           color: T.text, outline: 'none' },
  datumOn: { border: '1px solid #19c47f', color: T.text, background: 'rgba(25,196,127,0.14)' },
  placeBox: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 560 },
  placeHead: { fontSize: 11, fontWeight: 600, color: T.text },
  placeRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  step: { width: 15, height: 15, borderRadius: 4, flex: 'none', fontSize: 9.5,
          fontWeight: 700, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', background: 'rgba(57,135,229,0.18)', color: '#7fb3f0' },
  stepTodo: { border: `1px solid ${T.accent}`, background: 'color-mix(in srgb, var(--accent) 16%, transparent)', color: T.text },
  builtBox: { fontSize: 10.5, color: T.text, background: 'rgba(25,158,112,0.12)',
              border: '1px solid rgba(25,158,112,0.5)', borderRadius: 7,
              padding: '6px 9px', lineHeight: 1.5, maxWidth: 560 },
  floorChip: { display: 'inline-block', fontSize: 9.5, padding: '2px 7px',
               borderRadius: 10, marginRight: 5, background: 'rgba(255,255,255,0.07)',
               color: T.dim },
  clash: { fontSize: 10.5, color: T.text, background: 'rgba(224,172,74,0.12)',
           border: `1px solid ${T.warn}`, borderRadius: 7, padding: '6px 9px',
           lineHeight: 1.5, maxWidth: 560 },
  refusal: { fontSize: 10.5, color: T.text, background: 'color-mix(in srgb, var(--err) 12%, transparent)',
             border: `1px solid ${T.err}`, borderRadius: 7, padding: '6px 9px', lineHeight: 1.5, maxWidth: 560 },
  /* Square top: the header and the view row sit directly on it, and a rounded
     corner under a flat one reads as two panels that failed to line up. */
  canvasBox: { position: 'relative', border: `1px solid ${T.border}`,
               borderRadius: '0 0 8px 8px',
               overflow: 'hidden', background: 'var(--surface-3)' },
  traceBlock: { display: 'flex', flexDirection: 'column' },
  inspector: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
               background: T.surface, border: `1px solid ${T.border}`,
               borderRadius: 8, padding: '6px 9px' },
  select: { fontSize: 11, padding: '4px 7px', borderRadius: 6,
            border: `1px solid ${T.border}`, background: T.surface, color: T.text, outline: 'none' },
  readout: { fontSize: 11, fontWeight: 600, color: T.text },
  dim: { fontSize: 10, color: T.dim },
};
